import { NotFound } from "@wexinc/wex-common-web";
import { Heading } from "@wexinc/wex-ui";
import AppHeader from "app/AppHeader";
import { CopAdminRoutes, CopRoutes } from "app/routes";
import AdminLayout from "pages/administration/AdminLayout";
import Organizations from "pages/administration/Organizations";
import Users from "pages/administration/Users";
import InvitationsLanding from "pages/security/invitation/InvitationsLanding";
import Registration from "pages/security/invitation/Registration";
import IDPLogin from "pages/security/login/IDPLogin";
import LoginCallback from "pages/security/login/LoginCallback";
import LoginLanding from "pages/security/login/LoginLanding";
import PasswordReset from "pages/security/login/PasswordReset";
import PasswordResetRequest from "pages/security/login/PasswordResetRequest";
import UsernamePasswordLogin from "pages/security/login/UsernamePasswordLogin";
import React, { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { PortalAppProvider } from "services/portal-app/PortalAppProvider";
import { RolesProvider } from "services/security/RolesProvider";
import useCopSecurityService from "services/security/useCopSecurityService";
import Hub from "../pages/dashboard/Hub";
import AppBootstrapper from "./AppBootstrapper";

const NoOrgMessage = (
  <Heading
    size="small"
    className="max-w-[950px] my-4"
  >
    Unable to find the requested organization. Organization may be inactive or you may lack access.
  </Heading>
);

const ApplicationRoutes: React.FC = () => {
  const securityService = useCopSecurityService();
  const nodeRef = React.useRef(null);
  const location = useLocation();

  const isPublicPage =
    location.pathname.indexOf(CopRoutes.loginRoot) !== -1 ||
    location.pathname.indexOf(CopRoutes.invitationsRoot) !== -1 ||
    location.pathname.indexOf(CopRoutes.passwordRoot) !== -1;

  useEffect(() => {
    if (isPublicPage === false && securityService.securityStore.authStatus === "not-authenticated") {
      securityService.gotoLogin();
    }
    // eslint-disable-next-line
  }, [location, securityService.securityStore]);

  const isLoggingInForPortalApp = securityService.isLoggingInForPortalApp();

  return (
    <PortalAppProvider>
      <RolesProvider>
        <div className="h-screen text-neutral-dark-100 overflow-hidden">
          {securityService.securityStore.authStatus === "authenticated" &&
            isPublicPage === false &&
            isLoggingInForPortalApp === false && (
              <Routes>
                <Route
                  path={CopRoutes.root}
                  element={<AppHeader />}
                />
                <Route
                  path={`${CopRoutes.admin}/*`}
                  element={<AppHeader />}
                >
                  <Route
                    path={CopAdminRoutes.organizations}
                    element={<AppHeader />}
                  >
                    <Route
                      path={":organizationId"}
                      element={<AppHeader />}
                    />
                  </Route>
                  <Route
                    path={CopAdminRoutes.users}
                    element={<AppHeader />}
                  />
                </Route>
              </Routes>
            )}
          <div className="h-full relative">
            <TransitionGroup component={null}>
              <CSSTransition
                nodeRef={nodeRef}
                key={location.pathname}
                classNames="fade"
                timeout={1000}
              >
                <div
                  ref={nodeRef}
                  className="h-full w-full"
                  style={{
                    position: "relative",
                  }}
                >
                  <Routes location={location}>
                    <Route
                      path={CopRoutes.loginRoot}
                      element={<LoginLanding />}
                    />
                    {securityService.securityStore.authStatus === "not-authenticated" && (
                      <Route
                        path={CopRoutes.root}
                        element={<LoginLanding />}
                      />
                    )}
                    <Route
                      path={CopRoutes.idpLogin}
                      element={<IDPLogin />}
                    />
                    <Route
                      path={CopRoutes.copLogin}
                      element={<UsernamePasswordLogin />}
                    />
                    <Route
                      path={CopRoutes.loginCallback}
                      element={<LoginCallback />}
                    />
                    <Route
                      path={CopRoutes.invitation}
                      element={<InvitationsLanding />}
                    />
                    <Route
                      path={CopRoutes.invitationRegistration}
                      element={<Registration />}
                    />
                    <Route
                      path={CopRoutes.passwordResetRequest}
                      element={<PasswordResetRequest />}
                    />
                    <Route
                      path={CopRoutes.passwordReset}
                      element={<PasswordReset />}
                    />
                  </Routes>
                  {securityService.securityStore.authStatus === "authenticated" &&
                    isPublicPage === false &&
                    isLoggingInForPortalApp === false && (
                      <AppBootstrapper>
                        <Routes>
                          <Route
                            path={CopRoutes.root}
                            element={<Hub />}
                          />
                          <Route
                            path={`${CopRoutes.admin}`}
                            element={<AdminLayout />}
                          >
                            <Route
                              path={CopAdminRoutes.organizations}
                              element={<Organizations />}
                            >
                              <Route
                                path=":organizationId"
                                element={<Organizations />}
                              />
                            </Route>
                            <Route
                              path={CopAdminRoutes.users}
                              element={<Users />}
                            />
                          </Route>
                          <Route
                            path="/org-not-found"
                            element={
                              <NotFound
                                rootRoute={CopRoutes.root}
                                customMessage={NoOrgMessage}
                              />
                            }
                          />
                          <Route
                            path="*"
                            element={<NotFound rootRoute={CopRoutes.root} />}
                          />
                        </Routes>
                      </AppBootstrapper>
                    )}
                </div>
              </CSSTransition>
            </TransitionGroup>
          </div>
        </div>
        <Tooltip
          className="z-10 max-w-xs"
          disableStyleInjection="core"
        />
      </RolesProvider>
    </PortalAppProvider>
  );
};

export default ApplicationRoutes;
